import { useCallback, useEffect, useMemo, useState } from 'react';
import { ProjectKeywordGq } from '../../../../../graphql/projects/models/ProjectKeywordGq';
import { KeywordTableState } from './useKeywordTableState';

export interface DragState {
  dragActive: boolean;
  dragFinished: boolean;
  isSelecting: boolean;
  dragStartIndex: number;
  dragEndIndex: number;
}

export function useDragBag(keywordTableState: KeywordTableState, keywords: ProjectKeywordGq[]) {
  // Initiate state
  const [dragState, setDragState] = useState<DragState>({
    dragActive: false,
    dragFinished: false,
    isSelecting: false,
    dragStartIndex: 0,
    dragEndIndex: 0,
  });

  // Activate drag
  const onMouseDownKeyword = useCallback(
    (keywordId: number, keywordIndex: number, selecting: boolean) => {
      setDragState((prev) =>
        prev.dragActive
          ? prev
          : {
              dragActive: true,
              dragFinished: false,
              isSelecting: selecting,
              dragStartIndex: keywordIndex,
              dragEndIndex: keywordIndex,
            }
      );
    },
    []
  );

  // Hover a row
  const onMouseOverKeyword = useCallback((keywordId: number, keywordIndex: number) => {
    setDragState((prev) => (!prev.dragActive ? prev : { ...prev, dragEndIndex: keywordIndex }));
  }, []);

  // Deactivating drag
  const deactivateDrag = useCallback((endIndex?: number) => {
    if (endIndex === undefined) {
      setDragState({
        isSelecting: false,
        dragActive: false,
        dragStartIndex: 0,
        dragFinished: false,
        dragEndIndex: 0,
      });
      return;
    }

    setDragState((prev) => ({
      ...prev,
      dragEndIndex: endIndex,
      dragActive: false,
      dragFinished: true,
    }));
  }, []);

  // Transfer dragstate to select state
  // We do this via use effect to decouple the keyword rows from the keyword table state & keywords
  useEffect(() => {
    if (!dragState.dragFinished) return;
    setDragState({
      isSelecting: false,
      dragActive: false,
      dragStartIndex: 0,
      dragFinished: false,
      dragEndIndex: 0,
    });
    const keywordIds = keywords.slice(minKeywordIndex, maxKeywordIndex + 1).map((kw) => kw.id);
    keywordTableState.handleSelectMany(keywordIds, dragState.isSelecting);
  }, [dragState.dragFinished]);

  // Function for figuring out if row is selected
  const minKeywordIndex = Math.min(dragState.dragStartIndex, dragState.dragEndIndex);
  const maxKeywordIndex = Math.max(dragState.dragStartIndex, dragState.dragEndIndex);
  const isIndexSelected = useCallback(
    (index: number) => dragState.dragActive && index >= minKeywordIndex && index <= maxKeywordIndex,
    [dragState]
  );

  return {
    deactivateDragMode: deactivateDrag,
    onMouseOver: onMouseOverKeyword,
    onMouseDown: onMouseDownKeyword,
    isIndexSelected,
  };
}
